import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
  StatTile,
  Tabs,
  cn,
  type DataTableCard,
} from '@bcecloud/portal-ui';
import { LayersIcon } from 'lucide-react';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { LIST_QUERY_KEYS, useUrlSearch } from '@/lib/use-list-query-state';
import {
  DEFAULT_MIN_EVENTS,
  MIN_EVENTS,
  MIN_EVENTS_OPTIONS,
  parseMinEvents,
  withMinEvents,
  type MinEvents,
  PROGRAMS_PARAM,
  PROGRAMS_TAB,
  completenessText,
  formatMoney,
  formatPerDollar,
  formatPerUnit,
  formatPercent,
  isThin,
  parseProgramsTab,
  programDetailPath,
  readProgramLosers,
  readProgramsReport,
  windowText,
  type ProgramRow,
  type ProgramsQuery,
  type ProgramsReport,
  type ProgramsTab,
} from '@/lib/program-profitability';
import {
  ContributionNotes,
  EVENT_DEFAULT_SORT,
  FigureCell,
  MarginCell,
  MoneyCell,
  ProgramEventsTable,
  ProgramsFilters,
  useProgramsQuery,
} from '@/pages/program-profitability-shared';
import { ProgramProfitabilityDetailPage } from '@/pages/ProgramProfitabilityDetailPage';

const PROGRAM_SORT_FIELDS = {
  program: 'groupKey',
  events: 'events',
  participants: 'participants',
  revenue: 'revenue',
  cost: 'itemizedCost',
  commission: 'commission',
  ipadFees: 'ipadFees',
  contribution: 'contribution',
  margin: 'margin',
  revenuePerParticipant: 'revenuePerParticipant',
  contributionPerParticipant: 'contributionPerParticipant',
  perRevenueDollar: 'contributionPerRevenueDollar',
  medianMargin: 'medianEventMargin',
  belowTarget: 'belowTargetShare',
} as const;
const PROGRAM_SORT_KEYS = Object.values(PROGRAM_SORT_FIELDS);
const PROGRAM_DEFAULT_SORT = { key: PROGRAM_SORT_FIELDS.contribution, direction: 'desc' } as const;

/** Everything except the Program column is a number. */
const PROGRAM_COLUMNS = 14;
const PROGRAM_RIGHT_ALIGNED = Array.from({ length: PROGRAM_COLUMNS - 1 }, (_, i) => i + 1);

/** Keeps the window and mode when moving between the list and a program. */
function carriedSearch(params: URLSearchParams): string {
  const kept = new URLSearchParams();
  for (const key of [PROGRAMS_PARAM.from, PROGRAMS_PARAM.to, PROGRAMS_PARAM.mode]) {
    const value = params.get(key);
    if (value) kept.set(key, value);
  }
  const text = kept.toString();
  return text ? `?${text}` : '';
}

function SummaryTiles({ report }: { readonly report: ProgramsReport }): React.ReactElement {
  const totals = report.totals;
  return (
    <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
      <StatTile label="Contribution" value={formatMoney(totals.contribution)} hint={windowText(report.window)} />
      <StatTile label="Margin" value={formatPercent(totals.margin)} hint={`on ${formatMoney(totals.revenue)} revenue`} />
      <StatTile
        label="Contribution per participant"
        value={formatPerUnit(totals.contributionPerParticipant)}
        hint={`${totals.participants.toLocaleString('en-US')} participants`}
      />
      <StatTile label="Events" value={totals.events.toLocaleString('en-US')} hint={completenessText(totals)} />
    </div>
  );
}

function ProgramsTable({
  report,
  search,
}: {
  readonly report: ProgramsReport;
  readonly search: string;
}): React.ReactElement {
  const [params] = useSearchParams();
  const { sort, onSort } = useTableSort(PROGRAM_SORT_KEYS, PROGRAM_DEFAULT_SORT);
  const rows = useMemo(() => sortRows(filterRows(report.programs, search, ['groupKey']), sort), [report.programs, search, sort]);
  const carry = carriedSearch(params);
  const target = report.targetMargin;
  const targetLabel = `Below ${formatPercent(target)}`;

  if (rows.length === 0) {
    return (
      <Panel>
        <EmptyState title="No programs match" message="Try a different search." />
      </Panel>
    );
  }

  const cards: DataTableCard[] = rows.map((row) => ({
    key: row.groupKey,
    title: (
      <Link to={programDetailPath(row.groupKey, carry)} className="text-brand hover:underline">
        {row.groupKey}
      </Link>
    ),
    subtitle: `${row.events} events · ${completenessText(row)}`,
    fields: [
      { label: 'Contribution', value: formatMoney(row.contribution) },
      { label: 'Margin', value: formatPercent(row.margin) },
      { label: 'Per participant', value: formatPerUnit(row.contributionPerParticipant) },
      { label: 'Per $1 of revenue', value: formatPerDollar(row.contributionPerRevenueDollar) },
      { label: 'Revenue', value: formatMoney(row.revenue) },
      { label: targetLabel, value: formatPercent(row.belowTargetShare) },
    ],
  }));

  return (
    <DataTable
      headers={[
        { label: 'Program', sortKey: PROGRAM_SORT_FIELDS.program },
        { label: 'Events', sortKey: PROGRAM_SORT_FIELDS.events },
        { label: 'Participants', sortKey: PROGRAM_SORT_FIELDS.participants },
        { label: 'Revenue', sortKey: PROGRAM_SORT_FIELDS.revenue },
        { label: 'Itemized cost', sortKey: PROGRAM_SORT_FIELDS.cost },
        { label: 'Commission', sortKey: PROGRAM_SORT_FIELDS.commission },
        { label: 'iPad fees', sortKey: PROGRAM_SORT_FIELDS.ipadFees },
        { label: 'Contribution', sortKey: PROGRAM_SORT_FIELDS.contribution },
        { label: 'Margin', sortKey: PROGRAM_SORT_FIELDS.margin },
        { label: 'Revenue / person', sortKey: PROGRAM_SORT_FIELDS.revenuePerParticipant },
        { label: 'Contribution / person', sortKey: PROGRAM_SORT_FIELDS.contributionPerParticipant },
        { label: 'Per $1 of revenue', sortKey: PROGRAM_SORT_FIELDS.perRevenueDollar },
        { label: 'Median margin', sortKey: PROGRAM_SORT_FIELDS.medianMargin },
        { label: targetLabel, sortKey: PROGRAM_SORT_FIELDS.belowTarget },
      ]}
      rightAlignedHeaders={PROGRAM_RIGHT_ALIGNED}
      sort={sort}
      onSort={onSort}
      minWidth={1720}
      cards={cards}
    >
      {rows.map((row: ProgramRow) => (
        <tr key={row.groupKey} className="border-t border-portal-line align-middle">
          <td className="min-w-[220px] p-3 text-portal-small">
            <Link to={programDetailPath(row.groupKey, carry)} className="block font-bold text-brand hover:underline">
              {row.groupKey}
            </Link>
            <span className={cn('block text-portal-micro', isThin(row) ? 'text-amber-700' : 'text-portal-muted')}>
              {completenessText(row)}
            </span>
          </td>
          <FigureCell>{row.events.toLocaleString('en-US')}</FigureCell>
          <FigureCell>{row.participants.toLocaleString('en-US')}</FigureCell>
          <MoneyCell value={row.revenue} />
          <MoneyCell value={row.itemizedCost} />
          <FigureCell>{formatMoney(row.commission)}</FigureCell>
          <FigureCell>{formatMoney(row.ipadFees)}</FigureCell>
          <MoneyCell value={row.contribution} strong />
          <MarginCell margin={row.margin} target={target} />
          <FigureCell>{formatPerUnit(row.revenuePerParticipant)}</FigureCell>
          <td className="whitespace-nowrap p-3 text-right text-portal-small font-bold tabular-nums text-portal-ink">
            {formatPerUnit(row.contributionPerParticipant)}
          </td>
          <FigureCell>{formatPerDollar(row.contributionPerRevenueDollar)}</FigureCell>
          <MarginCell margin={row.medianEventMargin} target={target} />
          <FigureCell>{formatPercent(row.belowTargetShare)}</FigureCell>
        </tr>
      ))}
    </DataTable>
  );
}

function ProgramsView({ query }: { readonly query: ProgramsQuery }): React.ReactElement {
  const { search, setSearch } = useUrlSearch();
  const [params, setParams] = useSearchParams();
  const minEvents = parseMinEvents(params.get(PROGRAMS_PARAM.minEvents));
  function setMinEvents(next: MinEvents): void {
    setParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        if (next === DEFAULT_MIN_EVENTS) nextParams.delete(PROGRAMS_PARAM.minEvents);
        else nextParams.set(PROGRAMS_PARAM.minEvents, next);
        return nextParams;
      },
      { replace: true },
    );
  }
  const report = useQuery({
    queryKey: ['accounting-programs', query],
    queryFn: ({ signal }) => readProgramsReport(query, signal),
    placeholderData: keepPreviousData,
  });

  if (report.isError) return <ErrorNotice title="Programs unavailable" message={errorMessage(report.error)} />;
  if (report.isPending) return <SkeletonTable label="Loading programs…" />;
  if (report.data.programs.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={<LayersIcon aria-hidden className="size-5" />}
          title="No events in this window"
          message="Programs appear here once signed events in these dates have an income and expense statement. Widen the dates or switch to all events."
        />
      </Panel>
    );
  }
  const listed = withMinEvents(report.data.programs, minEvents);
  return (
    <>
      <SummaryTiles report={report.data} />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search programs"
          placeholder="Program name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        <SegmentedControl<MinEvents>
          label="Minimum events"
          value={minEvents}
          onChange={setMinEvents}
          options={MIN_EVENTS_OPTIONS}
        />
        <span className="text-portal-micro text-portal-muted">
          {listed.length} of {report.data.programs.length} programs
          {minEvents === MIN_EVENTS.all ? '' : ` with ${minEvents}+ events`} · margins under{' '}
          {formatPercent(report.data.targetMargin)} in amber, losses in red. The totals above count every program.
        </span>
      </div>
      <ProgramsTable report={{ ...report.data, programs: listed }} search={search} />
    </>
  );
}

function LosersView({ query }: { readonly query: ProgramsQuery }): React.ReactElement {
  const losers = useQuery({
    queryKey: ['accounting-program-losers', query],
    queryFn: ({ signal }) => readProgramLosers(query, signal),
    placeholderData: keepPreviousData,
  });
  const target = useQuery({
    queryKey: ['accounting-programs', query],
    queryFn: ({ signal }) => readProgramsReport(query, signal),
    placeholderData: keepPreviousData,
  });

  if (losers.isError) return <ErrorNotice title="Events unavailable" message={errorMessage(losers.error)} />;
  if (losers.isPending) return <SkeletonTable label="Loading events…" />;
  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Events that lost money" value={losers.data.negativeEvents.toLocaleString('en-US')} hint={windowText(losers.data.window)} />
        <StatTile label="Lost on them" value={formatMoney(losers.data.negativeContribution)} hint="Contribution below zero" />
        {target.data ? (
          <>
            <StatTile label="Portfolio contribution" value={formatMoney(target.data.totals.contribution)} hint={`${target.data.totals.events.toLocaleString('en-US')} events`} />
            <StatTile label="Events below target" value={formatPercent(target.data.totals.belowTargetShare)} hint={`Margin under ${formatPercent(target.data.targetMargin)}`} />
          </>
        ) : null}
      </div>
      <ProgramEventsTable
        events={losers.data.events}
        target={target.data?.targetMargin ?? 0}
        showProgram
        defaultSort={EVENT_DEFAULT_SORT.byContribution}
        searchLabel="Search the lowest-contribution events"
      />
    </>
  );
}

const TAB_ITEMS = [
  { key: PROGRAMS_TAB.programs, label: 'Programs' },
  { key: PROGRAMS_TAB.losers, label: 'Lowest contribution' },
] as const;

/**
 * Analytics → Accounting → Programs: event contribution per program since
 * 2023, from the Income & Expense Statements, plus the events that made the
 * least. Behind view_accounting (the API enforces it too).
 */
function ProgramsListPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const filters = useProgramsQuery();
  const tab = parseProgramsTab(params.get(PROGRAMS_PARAM.tab));

  function setTab(next: ProgramsTab): void {
    setParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        if (next === PROGRAMS_TAB.programs) nextParams.delete(PROGRAMS_PARAM.tab);
        else nextParams.set(PROGRAMS_PARAM.tab, next);
        // Each tab has its own columns and rows: start it unsorted and unfiltered.
        for (const key of ['sort', 'dir', LIST_QUERY_KEYS.search]) nextParams.delete(key);
        return nextParams;
      },
      { replace: true },
    );
  }

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ACCOUNTING"
        title="Programs"
        subtitle="What each program makes per event and per participant, and which events made the least."
      />
      <ProgramsFilters {...filters} />
      <ContributionNotes />
      <Tabs<ProgramsTab> label="Programs views" items={TAB_ITEMS} active={tab} onChange={setTab} />
      {tab === PROGRAMS_TAB.losers ? <LosersView query={filters.query} /> : <ProgramsView query={filters.query} />}
    </section>
  );
}

/** One route for the list and a program's detail, so App.tsx carries a single entry. */
export function ProgramProfitabilityPage(): React.ReactElement {
  const { groupKey } = useParams();
  return groupKey ? <ProgramProfitabilityDetailPage groupKey={groupKey} /> : <ProgramsListPage />;
}
